import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import InternalBackLink from '../Internal/InternalBackLink';
import './SpeckleBusinessCase.css';

/**
 * Money-first Speckle Team case — systems-honest (Inventor/Fusion gaps).
 * SoT: docs/SPECKLE_BUSINESS_CASE.md
 */

function Step({ n, title, sub, waste, ok, gap }) {
  const cls = ['proc-step', waste && 'waste', ok && 'ok', gap && 'gap'].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <span className="n">{n}</span>
      <b>{title}</b>
      {sub ? <small>{sub}</small> : null}
    </div>
  );
}

function Arrow() {
  return (
    <div className="proc-arrow" aria-hidden="true">
      →
    </div>
  );
}

function SystemsMap() {
  return (
    <div className="sys-map">
      <div className="sys-band">
        <div className="sys-band-lbl">
          <b>01 · Authoring</b>
          <span>what we design in</span>
        </div>
        <div className="sys-nodes">
          <div className="sys-node gap">
            <b>Inventor</b>
            <small>no Speckle connector</small>
          </div>
          <div className="sys-node gap">
            <b>SoftPlan</b>
            <small>no Speckle connector</small>
          </div>
          <div className="sys-node gap">
            <b>Fusion 360?</b>
            <small>no Speckle connector</small>
          </div>
        </div>
      </div>
      <div className="sys-band eng">
        <div className="sys-band-lbl">
          <b>02 · Bridge</b>
          <span>eng work · not free with $99</span>
        </div>
        <div className="sys-nodes">
          <div className="sys-node">
            <b>Export STEP / OBJ / DWG</b>
            <small>from Inventor / SoftPlan</small>
          </div>
          <div className="sys-node">
            <b>Speckle file upload</b>
            <small>or AutoCAD connector</small>
          </div>
          <div className="sys-node">
            <b>SOP + product hook</b>
            <small>~2 eng weeks base</small>
          </div>
        </div>
      </div>
      <div className="sys-band">
        <div className="sys-band-lbl">
          <b>03 · Where it lands</b>
          <span>what $99 actually rents</span>
        </div>
        <div className="sys-nodes">
          <div className="sys-node ok">
            <b>Speckle Team</b>
            <small>versions · share links</small>
          </div>
          <div className="sys-node">
            <b>Partner browser</b>
            <small>open the link</small>
          </div>
        </div>
      </div>
      <div className="sys-band stay">
        <div className="sys-band-lbl">
          <b>Stays ours</b>
          <span>not replaced by Speckle</span>
        </div>
        <div className="sys-nodes">
          <div className="sys-node">
            <b>/viewer R3F</b>
            <small>homeowner site planner</small>
          </div>
          <div className="sys-node">
            <b>/models/*.glb</b>
            <small>runtime meshes</small>
          </div>
          <div className="sys-node">
            <b>Neon + HubSpot</b>
            <small>CRM · proposals</small>
          </div>
          <div className="sys-node">
            <b>Google Maps</b>
            <small>site context · tiles</small>
          </div>
        </div>
      </div>
    </div>
  );
}

function CadGapTable() {
  return (
    <table className="sheet">
      <thead>
        <tr>
          <th>Our tool</th>
          <th>Official Speckle plug-in?</th>
          <th>How geometry gets in (today)</th>
        </tr>
      </thead>
      <tbody>
        <tr className="warn-row">
          <td>
            <strong>Inventor</strong>
          </td>
          <td className="money neg">No</td>
          <td>Export STEP / IGES / OBJ / STL / FBX / DWG → Speckle file upload (or DWG via AutoCAD connector)</td>
        </tr>
        <tr className="warn-row">
          <td>
            <strong>Fusion 360</strong>
          </td>
          <td className="money neg">No</td>
          <td>Same — export then upload. Community has asked; Speckle has not shipped a connector</td>
        </tr>
        <tr className="warn-row">
          <td>
            <strong>SoftPlan</strong>
          </td>
          <td className="money neg">No</td>
          <td>Our offline convert (`.dat` → OBJ/GLB). OBJ can upload to Speckle (geometry; materials weak)</td>
        </tr>
        <tr>
          <td>Revit / Rhino / AutoCAD / Civil 3D / Tekla…</td>
          <td className="money pos">Yes</td>
          <td>Native Speckle connectors — useful if partners use those tools later</td>
        </tr>
      </tbody>
    </table>
  );
}

function BridgeDiagram() {
  return (
    <div className="proc-pair">
      <div className="proc bad">
        <div className="proc-head">
          <b>Authoring → partner today</b>
          <span className="time">TRIBAL · WRONG FILE RISK</span>
        </div>
        <div className="proc-flow">
          <Step n="01" title="Inventor / SoftPlan" sub="source CAD" />
          <Arrow />
          <Step n="02" title="Someone exports" sub="ad-hoc" waste />
          <Arrow />
          <Step n="03" title="Email ZIP" sub="wrong rev?" waste />
          <Arrow />
          <Step n="04" title="Partner confused" sub="+ hours" waste />
        </div>
      </div>

      <div className="proc good">
        <div className="proc-head">
          <b>Proposed — Speckle + bridge</b>
          <span className="time">DOCUMENTED EXPORT · LINK</span>
        </div>
        <div className="proc-flow">
          <Step n="01" title="Inventor / SoftPlan" sub="unchanged" ok />
          <Arrow />
          <Step n="02" title="Export SOP" sub="STEP/OBJ/DWG" gap />
          <Arrow />
          <Step n="03" title="Speckle version" sub="file upload" ok />
          <Arrow />
          <Step n="04" title="Send link" sub="partner opens" ok />
        </div>
      </div>

      <div className="proc-save">
        <p>
          <strong>Amber/gap steps are eng + process work</strong> — not included in the $99. Base budget:{' '}
          <strong>~2 engineer weeks (~$14k)</strong> to document export, upload path, and a share hook in our tools.
        </p>
        <span className="badge">~$14k setup (one-time)</span>
      </div>
    </div>
  );
}

function HandoffDiagrams() {
  return (
    <div className="proc-pair">
      <div className="proc bad">
        <div className="proc-head">
          <b>Today — email / ZIP handoff</b>
          <span className="time">BASE MESSY CYCLE · ~2 HRS LOST</span>
        </div>
        <div className="proc-flow">
          <Step n="01" title="Have a model" sub="somewhere" />
          <Arrow />
          <Step n="02" title="Export / zip" sub="+15–30 min" waste />
          <Arrow />
          <Step n="03" title="Email partner" sub="+10 min" waste />
          <Arrow />
          <Step n="04" title="Wrong file?" sub="+20–40 min" waste />
          <Arrow />
          <Step n="05" title="Resend + explain" sub="+30–60 min" waste />
          <Arrow />
          <Step n="06" title="Finally reviews" sub="end of ~2 hrs" />
        </div>
      </div>

      <div className="proc good">
        <div className="proc-head">
          <b>With Speckle — after bridge exists</b>
          <span className="time">CLEAN CYCLE · ~10–15 MIN</span>
        </div>
        <div className="proc-flow">
          <Step n="01" title="Model in Speckle" sub="via export SOP" ok />
          <Arrow />
          <Step n="02" title="Publish version" sub="~2 min" ok />
          <Arrow />
          <Step n="03" title="Send link" sub="~1 min" ok />
          <Arrow />
          <Step n="04" title="Partner reviews" sub="right model" ok />
          <Arrow />
          <Step n="05" title="Comment / done" sub="~10–15 min total" ok />
        </div>
      </div>

      <div className="proc-save">
        <p>
          <strong>Ops time we remove</strong> once the model is already in Speckle. Does <em>not</em> include Inventor export time —
          that is the bridge SOP above.
        </p>
        <span className="badge">~2 hrs saved / messy cycle</span>
      </div>
    </div>
  );
}

/** Top Speckle integrations ranked for Construx — docs/SPECKLE_BUSINESS_CASE.md */
const SPECKLE_USE_CASES = [
  {
    num: '01',
    name: 'File uploads',
    kind: 'Upload',
    tag: 'Day-1 bridge',
    blurb:
      'Primary Inventor / SoftPlan path. Export STEP, OBJ, IFC, DWG, FBX… → drop into Speckle → version + partner link. No native CAD plug-in required.',
    apply: 'Get geometry in without waiting on an Inventor connector.',
  },
  {
    num: '02',
    name: 'AutoCAD',
    kind: 'Connector',
    tag: 'Inventor middleware',
    blurb:
      'When mesh upload is not enough: export DWG from Inventor, publish through Speckle’s AutoCAD connector for richer CAD structure and optional round-trip.',
    apply: 'Upgrade path after basic file upload is working.',
  },
  {
    num: '03',
    name: 'IFC',
    kind: 'Open BIM',
    tag: 'Partner interchange',
    blurb:
      'Hand a neutral IFC to GCs, engineers, or reviewers who will never open Inventor. Speckle keeps versions and a browser view on that file.',
    apply: 'Jurisdiction / consultant handoff without seat sprawl.',
  },
  {
    num: '04',
    name: 'Revit',
    kind: 'Connector',
    tag: 'AEC partners',
    blurb:
      'Architect or consultant partners live in Revit: load Construx reference geometry, comment, push revisions into Speckle for our ops to review.',
    apply: 'When a build partner’s design team is Revit-native.',
  },
  {
    num: '05',
    name: 'Power BI',
    kind: 'Connector',
    tag: 'Ops reporting',
    blurb:
      'Pull model / version metrics into Power BI for counts, status, and simple portfolio views — without inventing a full BI layer on Neon alone.',
    apply: 'Leadership dashboards once models are flowing.',
  },
  {
    num: '06',
    name: 'Rhino',
    kind: 'Connector',
    tag: 'Design studies',
    blurb:
      'Stage geometry for panelization or manufacturing conversations. Optional parallel path next to SoftPlan → GLB for the homeowner viewer.',
    apply: 'Internal design R&D and fabricator talks.',
  },
  {
    num: '07',
    name: 'Grasshopper',
    kind: 'Connector',
    tag: 'Rules / takeoff',
    blurb:
      'Parametric checks (area, span, part counts) published as Speckle versions for review — experiments before we hard-code estimators.',
    apply: 'Prototype BOM / constraint logic.',
  },
  {
    num: '08',
    name: 'Blender',
    kind: 'Connector',
    tag: 'Proposal viz',
    blurb:
      'Pull a Speckle model into Blender for stills or short clips used in backyard proposals and marketing — without rebuilding assets from scratch.',
    apply: 'Sales / proposal visuals.',
  },
  {
    num: '09',
    name: 'Navisworks',
    kind: 'Connector',
    tag: 'GC coordination',
    blurb:
      'When a build partner coordinates in Navis: publish the Construx model into their set so we are not the odd ZIP in the pile.',
    apply: 'Multi-trade jobs with a GC already on Navis.',
  },
  {
    num: '10',
    name: 'ACC',
    kind: 'Cloud sync',
    tag: 'Enterprise later',
    blurb:
      'Autodesk Construction Cloud / Forma Data Management sync (Team+). Useful if a partner or enterprise customer’s Revit already lives in ACC — not our day-1 path.',
    apply: 'Only when a real ACC customer shows up.',
  },
];

function IntegrationCards() {
  return (
    <div className="int-grid">
      {SPECKLE_USE_CASES.map((item) => (
        <article key={item.num} className="int-card">
          <div className="int-card-top">
            <span className="int-num">{item.num}</span>
            <span className="int-tag">{item.tag}</span>
          </div>
          <h3>{item.name}</h3>
          <p className="int-kind">{item.kind}</p>
          <p>{item.blurb}</p>
          <p className="int-apply">
            <strong>Use:</strong> {item.apply}
          </p>
        </article>
      ))}
    </div>
  );
}

function DiyDiagram() {
  return (
    <>
      <div className="diy-bars">
        <div className="diy-row">
          <div className="lbl">
            Build share + versions ourselves
            <small>base · 6 eng weeks</small>
          </div>
          <div className="diy-track">
            <div className="diy-fill bad">CABINET + UI + BUGS + MAINTAIN…</div>
          </div>
          <div className="amt bad">$42,000</div>
        </div>
        <div className="diy-steps">
          <span className="diy-chip waste">Build versioning product</span>
          <span className="diy-chip waste">Build share links</span>
          <span className="diy-chip waste">Still need Inventor export</span>
          <span className="diy-chip waste">Maintain forever</span>
        </div>

        <div className="diy-row">
          <div className="lbl">
            Speckle + bridge
            <small>sub + ~2 eng weeks</small>
          </div>
          <div className="diy-track">
            <div className="diy-fill good" style={{ width: '42%' }}>
              BRIDGE + $99/MO
            </div>
          </div>
          <div className="amt good">~$17.6k / 3 yr</div>
        </div>
        <div className="diy-steps">
          <span className="diy-chip">Export SOP (Inventor / SoftPlan)</span>
          <span className="diy-chip">File upload / API into Speckle</span>
          <span className="diy-chip">Share links (rented)</span>
          <span className="diy-chip">$1,188/yr ongoing</span>
        </div>
      </div>
      <div className="proc-save">
        <p>
          <strong>3-year base:</strong> Speckle path ≈ $14k bridge + $3.6k subs ≈ <strong>$17.6k</strong>. DIY cabinet alone ≈{' '}
          <strong>$42k</strong> before we invent partner viewing. Speckle still wins — but it is not “flip a switch.”
        </p>
        <span className="badge">~$24k less than DIY / 3 yr</span>
      </div>
    </>
  );
}

export default function SpeckleBusinessCase() {
  return (
    <div className="spk-case">
      <Helmet>
        <title>Speckle $99/mo — money case · Construx</title>
        <meta
          name="description"
          content="Honest Speckle Team business case: Inventor/Fusion have no connectors, real Construx backends, integration eng cost, ROI math."
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="page">
        <div className="doc-nav">
          <InternalBackLink className="doc-back" />
        </div>
        <header className="doc-head">
          <div className="doc-mark" aria-hidden="true" />
          <div>
            <div className="doc-title">Speckle for Construx — the money case</div>
            <div className="doc-sub">
              Dollars first. Honest about Inventor, SoftPlan, Fusion, and the eng work still required.
            </div>
          </div>
          <div className="doc-meta">
            <b>Decision brief</b>
            Jul 19 2026 · rev 2
          </div>
        </header>

        <div className="ask">
          <div>
            <div className="kicker">The ask</div>
            <h2>$99/mo Speckle Team + ≤2 eng weeks to wire it</h2>
            <p>
              Subscription is <strong>$1,188/year</strong>. Setup is about <strong>$14k</strong> (base) so Inventor / SoftPlan models can
              actually get into Speckle. Speckle does <strong>not</strong> plug into Inventor or Fusion 360 today.
            </p>
          </div>
          <div className="price-block">
            <div className="amt">
              $99<small>/mo</small>
            </div>
            <div className="yr">+ ~$14k bridge eng</div>
          </div>
        </div>

        <div className="card warn" style={{ marginBottom: 40 }}>
          <h3>Do not sell this as “already Speckle-compatible”</h3>
          <p>
            Our CAD stack is Inventor + SoftPlan (+ maybe Fusion). Speckle has <strong>no official connectors</strong> for those.
            Homeowner UX stays on our R3F viewer and GLBs. Neon / HubSpot / Google Maps stay put. Speckle is a rented filing cabinet —
            we still build the door from Inventor.
          </p>
        </div>

        {/* 01 Systems */}
        <section>
          <h2 className="sec">
            <span className="num">01</span> What we actually run today
          </h2>
          <p>Three bands: authoring (ours) → bridge (eng) → Speckle (rented). Bottom row is systems Speckle does not replace.</p>
          <SystemsMap />
        </section>

        {/* 02 CAD gap */}
        <section>
          <h2 className="sec">
            <span className="num">02</span> Inventor &amp; Fusion — connector reality check
          </h2>
          <p>
            Checked against Speckle’s published connector list (
            <a href="https://docs.speckle.systems/connectors/overview" target="_blank" rel="noreferrer">
              docs
            </a>
            ,{' '}
            <a href="https://speckle.systems/connectors/" target="_blank" rel="noreferrer">
              integrations
            </a>
            ). Autodesk has its own Inventor “Data Exchange” product — that is <strong>not</strong> Speckle.
          </p>
          <CadGapTable />
          <p className="callout">
            File upload can still take STEP, IGES, OBJ, STL, FBX, DWG, IFC, etc. So Speckle is usable — as a{' '}
            <strong>export destination</strong>, not a one-click Inventor plug-in.
          </p>
        </section>

        {/* 03 Bridge process */}
        <section>
          <h2 className="sec">
            <span className="num">03</span> Process — getting CAD into Speckle
          </h2>
          <p>This is the work the $99 does <em>not</em> buy. Amber = pain today. Blue-ish gap = eng/process we must fund.</p>
          <BridgeDiagram />
          <table className="sheet">
            <thead>
              <tr>
                <th>Bridge eng</th>
                <th>Conservative</th>
                <th>Base</th>
                <th>Upside</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Engineer weeks</td>
                <td>1</td>
                <td>
                  <strong>2</strong>
                </td>
                <td>4</td>
              </tr>
              <tr>
                <td>@ $175/hr</td>
                <td className="money">$7,000</td>
                <td className="money">
                  <strong>$14,000</strong>
                </td>
                <td className="money">$28,000</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* 04 Break-even sub only */}
        <section>
          <h2 className="sec">
            <span className="num">04</span> Subscription break-even (ops hours only)
          </h2>
          <p>
            Ignoring setup eng for a moment: the <strong>$1,188/yr</strong> sub alone breaks even at these staff hours.
          </p>
          <div className="hero-math">
            <div className="hero-stat">
              <div className="hero-num">11.9</div>
              <div className="hero-lbl">hours / year @ $100/hr</div>
              <div className="hero-note">pays the subscription only</div>
            </div>
            <div className="hero-stat">
              <div className="hero-num">~$14k</div>
              <div className="hero-lbl">bridge eng (base)</div>
              <div className="hero-note">one-time · year-1 investment</div>
            </div>
            <div className="hero-stat">
              <div className="hero-num">$297</div>
              <div className="hero-lbl">90-day sub trial</div>
              <div className="hero-note">cancel if unused</div>
            </div>
          </div>
        </section>

        {/* 05 Assumptions */}
        <section>
          <h2 className="sec">
            <span className="num">05</span> Assumptions
          </h2>
          <table className="sheet">
            <thead>
              <tr>
                <th>What we assumed</th>
                <th>Conservative</th>
                <th>Base</th>
                <th>Upside</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Loaded staff rate (ops)</td>
                <td className="money">$75/hr</td>
                <td className="money">$100/hr</td>
                <td className="money">$125/hr</td>
              </tr>
              <tr>
                <td>Partner review cycles / year</td>
                <td>12</td>
                <td>24</td>
                <td>48</td>
              </tr>
              <tr>
                <td>Hours lost / messy handoff</td>
                <td>1.0</td>
                <td>2.0</td>
                <td>3.0</td>
              </tr>
              <tr>
                <td>% cycles messy without Speckle</td>
                <td>40%</td>
                <td>50%</td>
                <td>60%</td>
              </tr>
              <tr>
                <td>Eng weeks — DIY full share cabinet</td>
                <td>3</td>
                <td>6</td>
                <td>12</td>
              </tr>
              <tr className="hl">
                <td>Eng weeks — Speckle bridge (export SOP + upload)</td>
                <td>1</td>
                <td>
                  <strong>2</strong>
                </td>
                <td>4</td>
              </tr>
              <tr>
                <td>Eng loaded rate</td>
                <td className="money">$150/hr</td>
                <td className="money">$175/hr</td>
                <td className="money">$200/hr</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* 06 Handoff */}
        <section>
          <h2 className="sec">
            <span className="num">06</span> Ops savings — partner handoff (after bridge)
          </h2>
          <HandoffDiagrams />
          <table className="sheet">
            <thead>
              <tr>
                <th></th>
                <th>Conservative</th>
                <th>Base</th>
                <th>Upside</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="k">calc</td>
                <td className="mono small">12 × 40% × 1 × $75</td>
                <td className="mono small">24 × 50% × 2 × $100</td>
                <td className="mono small">48 × 60% × 3 × $125</td>
              </tr>
              <tr>
                <td>Annual ops waste avoided</td>
                <td className="money">$360</td>
                <td className="money">
                  <strong>$2,400</strong>
                </td>
                <td className="money">$10,800</td>
              </tr>
              <tr>
                <td>Speckle sub only</td>
                <td className="money">($1,188)</td>
                <td className="money">($1,188)</td>
                <td className="money">($1,188)</td>
              </tr>
              <tr className="hl">
                <td>
                  <strong>Net vs sub (ignore setup)</strong>
                </td>
                <td className="money neg">−$828</td>
                <td className="money pos">
                  <strong>+$1,212</strong>
                </td>
                <td className="money pos">+$9,612</td>
              </tr>
            </tbody>
          </table>
          <p>Base: ops cleanup covers the <strong>subscription</strong>. It does not pay back the bridge eng in year one by itself.</p>
        </section>

        {/* 07 DIY */}
        <section>
          <h2 className="sec">
            <span className="num">07</span> Don&apos;t rebuild the cabinet
          </h2>
          <p>DIY still needs an Inventor export path. Speckle rents the cabinet; we only build the bridge.</p>
          <DiyDiagram />
          <table className="sheet">
            <thead>
              <tr>
                <th>3-year totals (base)</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>DIY share + versioning product</td>
                <td className="money neg">$42,000+</td>
              </tr>
              <tr>
                <td>Speckle bridge eng</td>
                <td className="money">$14,000</td>
              </tr>
              <tr>
                <td>Speckle Team × 3 years</td>
                <td className="money">$3,564</td>
              </tr>
              <tr className="hl">
                <td>
                  <strong>Speckle path total</strong>
                </td>
                <td className="money pos">
                  <strong>~$17,564</strong>
                </td>
              </tr>
              <tr className="hl">
                <td>
                  <strong>Saved vs DIY</strong>
                </td>
                <td className="money pos">
                  <strong>~$24,000+</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* 08 Scorecard */}
        <section>
          <h2 className="sec">
            <span className="num">08</span> Year-1 honest scorecard
          </h2>
          <p>Lead with this. Year 1 is an investment. The comparison is “Speckle + bridge” vs “build the cabinet ourselves.”</p>
          <table className="sheet big">
            <thead>
              <tr>
                <th>Line</th>
                <th>Conservative</th>
                <th>Base</th>
                <th>Upside</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Ops handoff savings</td>
                <td className="money">$360</td>
                <td className="money">$2,400</td>
                <td className="money">$10,800</td>
              </tr>
              <tr>
                <td>Speckle subscription</td>
                <td className="money">($1,188)</td>
                <td className="money">($1,188)</td>
                <td className="money">($1,188)</td>
              </tr>
              <tr>
                <td>Bridge eng (one-time)</td>
                <td className="money">($7,000)</td>
                <td className="money">
                  <strong>($14,000)</strong>
                </td>
                <td className="money">($28,000)</td>
              </tr>
              <tr className="hl">
                <td>
                  <strong>Year-1 cash net</strong>
                </td>
                <td className="money neg">−$7.8k</td>
                <td className="money neg">
                  <strong>−$12.8k</strong>
                </td>
                <td className="money neg">−$18.4k</td>
              </tr>
              <tr>
                <td>Memo: DIY cabinet cost avoided</td>
                <td className="money">$18k</td>
                <td className="money">$42k</td>
                <td className="money">$96k</td>
              </tr>
              <tr className="hl">
                <td>
                  <strong>Vs DIY in year 1</strong>
                </td>
                <td className="money pos">DIY ~$10k+ worse</td>
                <td className="money pos">
                  <strong>DIY ~$29k worse</strong>
                </td>
                <td className="money pos">DIY ~$78k worse</td>
              </tr>
            </tbody>
          </table>
          <div className="hero-math tight">
            <div className="hero-stat">
              <div className="hero-num">−$13k</div>
              <div className="hero-lbl">base year-1 cash</div>
              <div className="hero-note">investment · not free lunch</div>
            </div>
            <div className="hero-stat go">
              <div className="hero-num">~$29k</div>
              <div className="hero-lbl">cheaper than DIY</div>
              <div className="hero-note">same year · base case</div>
            </div>
            <div className="hero-stat go">
              <div className="hero-num">+$1.2k</div>
              <div className="hero-lbl">years 2–3 / yr</div>
              <div className="hero-note">after bridge is done</div>
            </div>
          </div>
        </section>

        {/* 09 Kill */}
        <section>
          <h2 className="sec">
            <span className="num">09</span> 90-day kill switch
          </h2>
          <p>
            Sub trial: <strong>$297</strong>. Cap bridge eng at 2 weeks. Keep Speckle if <strong>any two</strong>:
          </p>
          <ol className="steps">
            <li>
              <b>Documented Inventor or SoftPlan → Speckle export used on a real model</b>
              Proves the bridge works — not vapor.
            </li>
            <li>
              <b>Partner reviews from a Speckle link</b>
              No ZIP attach.
            </li>
            <li>
              <b>History kept past 30 days on a live project</b>
              Free plan is not enough.
            </li>
          </ol>
        </section>

        {/* 10 Decision */}
        <section>
          <h2 className="sec">
            <span className="num">10</span> Decision
          </h2>
          <div className="card go">
            <h3>Recommend: $99/mo + ≤2 eng weeks for the bridge</h3>
            <p>
              Do <strong>not</strong> claim Inventor or Fusion are Speckle-native. Claim: renting Speckle’s cabinet + a small export bridge
              is still ~$24k+ cheaper over 3 years than building share/versioning ourselves, and years 2–3 run ~+$1.2k/yr on ops savings
              alone (base). Cancel at 90 days if the bridge never gets used.
            </p>
          </div>
        </section>

        {/* 11 Integration cards */}
        <section>
          <h2 className="sec">
            <span className="num">11</span> Speckle integrations — Construx use cases
          </h2>
          <p>
            Speckle ships many connectors. These are the <strong>top 10 for us</strong>, ranked for Inventor / SoftPlan / partners — not a
            generic AEC brochure. Full list:{' '}
            <a href="https://speckle.systems/connectors/" target="_blank" rel="noreferrer">
              speckle.systems/connectors
            </a>
            .
          </p>
          <IntegrationCards />
          <p className="footnote" style={{ marginTop: 20 }}>
            Not in the top 10 for Construx today: Tekla, ETABS, Plant 3D, Civil 3D, Archicad, Vectorworks, SketchUp, Databricks, Snowflake,
            ProjectWise. Revisit if a partner stack demands them. Inventor / Fusion stay on <strong>file upload + AutoCAD</strong> until
            Speckle ships connectors.
          </p>
        </section>

        <div className="next">
          <div>
            <h3>Next</h3>
            <p>Approve sub → write Inventor/SoftPlan export SOP → upload one real model → partner link test → day-90 review.</p>
          </div>
          <div className="cta-row">
            <a className="cta" href="https://speckle.systems/connectors/" target="_blank" rel="noreferrer">
              Speckle integrations →
            </a>
            <a className="cta ghost" href="https://docs.speckle.systems/connectors/overview" target="_blank" rel="noreferrer">
              Connector docs
            </a>
            <Link className="cta ghost" to="/viewer">
              Product viewer
            </Link>
          </div>
        </div>

        <p className="footnote">
          Systems and math: <span className="mono">docs/SPECKLE_BUSINESS_CASE.md</span>. Connector status as of Jul 2026 from Speckle docs —
          re-check before purchase. Autodesk Data Exchange for Inventor is a separate Autodesk product, not Speckle. Planning numbers, not
          audited financials. Page requires <span className="mono">@buildconstrux.com</span> domain auth.
        </p>
      </div>
    </div>
  );
}
